import { Fragment, InputHTMLAttributes, ReactNode } from 'react';
import { twMerge } from 'tailwind-merge';
import { VariantProps } from 'tailwind-variants';
import { Divider, IsVisible } from '../../../../utils';
import { FieldBase, IFieldBaseProps } from '../../Base';
import { radioStyles } from '../styles';
import { IRadioFieldIconProps, RadioFieldIcon } from '../utils/Icon';

export interface IRadioGroupFieldOptions<T = number | string> {
  children?: ReactNode;
  imageUrl?: string;
  label?: string;
  value: T;
}

export interface IRadioGroupFieldProps
  extends Omit<InputHTMLAttributes<HTMLInputElement>, 'name' | 'type'>,
    VariantProps<typeof radioStyles.input> {
  containerProps?: {
    className?: string;
    rest?: Omit<IFieldBaseProps, 'className'>;
  };
  dividerProps?: {
    hide?: boolean;
    orientation?: 'horizontal' | 'vertical';
  };
  iconProps?: IRadioFieldIconProps;
  labelProps?: {
    bold?: boolean;
    className?: string;
    color?: string;
    italic?: boolean;
    underline?: boolean;
  };
  name: string;
  options: IRadioGroupFieldOptions[];
  shouldInjectAsHTML?: boolean;
}

export function RadioGroupField({
  className,
  containerProps,
  design,
  disabled = false,
  dividerProps = {
    hide: false,
    orientation: 'horizontal',
  },
  iconProps,
  inputSize = 'md',
  labelProps,
  name,
  onChange,
  options,
  readOnly = false,
  shouldInjectAsHTML = false,
  value,
  ...rest
}: IRadioGroupFieldProps) {
  return (
    <FieldBase
      className={twMerge('px-0', containerProps?.className)}
      iconPosition="right"
      {...containerProps?.rest}
    >
      {options.map(option => (
        <Fragment key={`${name}-${option.value}`}>
          <label
            className={radioStyles.label({
              className: labelProps?.className,
              design,
              disabled: disabled || readOnly,
              errored: !!containerProps?.rest?.error,
              size: inputSize,
              bold: labelProps?.bold,
              italic: labelProps?.italic,
              underline: labelProps?.underline,
            })}
            htmlFor={`${name}-${option.value}`}
            style={{ color: labelProps?.color }}
          >
            <IsVisible when={!!option.children}>{option.children}</IsVisible>
            <IsVisible when={!!option.label}>
              <IsVisible when={!shouldInjectAsHTML}>{option.label}</IsVisible>
              <IsVisible when={!!shouldInjectAsHTML}>
                <span
                  dangerouslySetInnerHTML={{
                    __html: option.label as string,
                  }}
                />
              </IsVisible>
            </IsVisible>

            <input
              checked={value === option.value}
              className={radioStyles.input({
                className,
                design,
                disabled: disabled || readOnly,
                errored: !!containerProps?.rest?.error,
                inputSize,
              })}
              disabled={disabled}
              id={`${name}-${option.value}`}
              name={name}
              onChange={onChange}
              readOnly={readOnly}
              type="radio"
              value={option.value}
              {...rest}
            />

            <RadioFieldIcon
              className={iconProps?.className}
              design={design}
              disabled={disabled || readOnly}
              rest={iconProps?.rest}
              size={inputSize}
            />
          </label>

          <IsVisible when={!dividerProps.hide}>
            <Divider
              className="bg-zinc-200 my-0"
              orientation={dividerProps.orientation}
            />
          </IsVisible>
        </Fragment>
      ))}
    </FieldBase>
  );
}
